import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Dumbbell, X } from "lucide-react";
import { api } from "../lib/api";

const DAYS_FMT = { weekday: "short", month: "short", day: "numeric" };

export default function Workouts() {
  const [workouts, setWorkouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [duration, setDuration] = useState(60);
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState([{ exercise: "", sets: 3, reps: 10, weight: 20 }]);
  const [busy, setBusy] = useState(false);

  const load = async () => setWorkouts((await api.get("/workouts")).data);
  useEffect(() => { load(); }, []);

  const updateRow = (i, field, val) => {
    const next = [...rows];
    next[i][field] = field === "exercise" ? val : Number(val);
    setRows(next);
  };
  const addRow = () => setRows([...rows, { exercise: "", sets: 3, reps: 10, weight: 20 }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));

  const reset = () => {
    setTitle(""); setDuration(60); setNotes("");
    setRows([{ exercise: "", sets: 3, reps: 10, weight: 20 }]);
    setDate(new Date().toISOString().slice(0, 10));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/workouts", {
        title, date, duration_min: Number(duration), notes,
        exercises: rows.filter((r) => r.exercise.trim()),
      });
      toast.success("Workout logged.");
      setOpen(false); reset(); load();
    } catch (err) {
      toast.error("Could not save workout");
    } finally { setBusy(false); }
  };

  const remove = async (id) => {
    await api.delete(`/workouts/${id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#FF3B30] mb-2">Training Log</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-none">Workouts</h1>
        </div>
        <button data-testid="open-workout-modal" onClick={() => setOpen(true)} className="bg-[#FF3B30] hover:bg-[#FF453A] text-white font-bold uppercase tracking-wide text-sm px-5 py-3 flex items-center gap-2 transition-transform duration-150 hover:-translate-y-0.5">
          <Plus size={18} /> Log Workout
        </button>
      </div>

      {workouts.length === 0 ? (
        <div className="border border-dashed border-white/10 py-24 text-center text-white/40">
          <Dumbbell size={40} className="mx-auto mb-4 text-white/20" />
          No workouts yet. Log your first session.
        </div>
      ) : (
        <div className="space-y-3" data-testid="workout-list">
          {workouts.map((w) => (
            <div key={w.id} className="p-5 bg-[#121212] border border-white/10 hover:border-white/25 transition-colors">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-display font-bold text-xl uppercase tracking-tight">{w.title}</h3>
                    <span className="text-xs text-white/40">{new Date(w.date).toLocaleDateString("en-US", DAYS_FMT)}</span>
                    {w.duration_min > 0 && <span className="text-xs bg-[#1A1A1A] px-2 py-1 text-white/60">{w.duration_min} min</span>}
                  </div>
                  {w.exercises?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {w.exercises.map((e, i) => (
                        <span key={i} className="text-xs bg-[#0A0A0A] border border-white/10 px-2.5 py-1.5 text-white/70">
                          <span className="text-white font-semibold">{e.exercise}</span> · {e.sets}×{e.reps} @ {e.weight}kg
                        </span>
                      ))}
                    </div>
                  )}
                  {w.notes && <p className="text-sm text-white/40 mt-2">{w.notes}</p>}
                </div>
                <button data-testid={`delete-workout-${w.id}`} onClick={() => remove(w.id)} className="text-white/30 hover:text-[#FF3B30] transition-colors"><Trash2 size={18} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)}>
          <div className="w-full max-w-2xl bg-[#121212] border border-white/15 max-h-[90vh] overflow-y-auto fade-up" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10 sticky top-0 bg-[#121212]">
              <h2 className="font-display font-black text-2xl uppercase tracking-tight">New Workout</h2>
              <button onClick={() => setOpen(false)} className="text-white/50 hover:text-white"><X /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-5">
              <div className="grid sm:grid-cols-3 gap-4">
                <div className="sm:col-span-2">
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Title</label>
                  <input data-testid="workout-title" value={title} onChange={(e) => setTitle(e.target.value)} required placeholder="Push Day" className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#FF3B30] focus:outline-none" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Duration (min)</label>
                  <input data-testid="workout-duration" type="number" value={duration} onChange={(e) => setDuration(e.target.value)} className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#FF3B30] focus:outline-none" />
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Date</label>
                <input data-testid="workout-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#FF3B30] focus:outline-none" />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Exercises</label>
                  <button type="button" data-testid="add-exercise-row" onClick={addRow} className="text-[#FF3B30] text-xs font-bold uppercase flex items-center gap-1 hover:underline"><Plus size={14} /> Add</button>
                </div>
                <div className="space-y-2">
                  <div className="grid grid-cols-12 gap-2 text-[10px] uppercase tracking-widest text-white/40 px-1">
                    <span className="col-span-5">Exercise</span><span className="col-span-2">Sets</span><span className="col-span-2">Reps</span><span className="col-span-2">Kg</span>
                  </div>
                  {rows.map((r, i) => (
                    <div key={i} className="grid grid-cols-12 gap-2 items-center">
                      <input data-testid={`ex-name-${i}`} value={r.exercise} onChange={(e) => updateRow(i, "exercise", e.target.value)} placeholder="Bench Press" className="col-span-5 bg-[#0A0A0A] border border-white/10 px-2.5 py-2 text-sm focus:border-[#FF3B30] focus:outline-none" />
                      <input type="number" value={r.sets} onChange={(e) => updateRow(i, "sets", e.target.value)} className="col-span-2 bg-[#0A0A0A] border border-white/10 px-2 py-2 text-sm focus:border-[#FF3B30] focus:outline-none" />
                      <input type="number" value={r.reps} onChange={(e) => updateRow(i, "reps", e.target.value)} className="col-span-2 bg-[#0A0A0A] border border-white/10 px-2 py-2 text-sm focus:border-[#FF3B30] focus:outline-none" />
                      <input type="number" value={r.weight} onChange={(e) => updateRow(i, "weight", e.target.value)} className="col-span-2 bg-[#0A0A0A] border border-white/10 px-2 py-2 text-sm focus:border-[#FF3B30] focus:outline-none" />
                      <button type="button" onClick={() => removeRow(i)} className="col-span-1 text-white/30 hover:text-[#FF3B30]"><X size={16} /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-bold uppercase tracking-[0.2em] text-white/50">Notes</label>
                <textarea data-testid="workout-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="mt-2 w-full bg-[#0A0A0A] border border-white/10 px-3 py-2.5 focus:border-[#FF3B30] focus:outline-none resize-none" />
              </div>

              <button data-testid="save-workout" disabled={busy} className="w-full bg-[#FF3B30] hover:bg-[#FF453A] disabled:opacity-50 text-white font-bold uppercase tracking-wide px-6 py-3 transition-transform duration-150 hover:-translate-y-0.5">
                {busy ? "Saving..." : "Save Workout"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
